import React, { useEffect, useRef } from 'react';

function AddressAutocomplete({ value, onChange, onPlaceSelected, placeholder, className, required }) {
  const addressInputRef = useRef(null);
  const onPlaceSelectedRef = useRef(onPlaceSelected);
  const onChangeRef = useRef(onChange);

  // Keep the callback ref up-to-date to avoid stale closures inside the maps event listener
  useEffect(() => {
    onPlaceSelectedRef.current = onPlaceSelected;
  }, [onPlaceSelected]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    const initAutocomplete = () => {
      if (!window.google || !addressInputRef.current) return;
      
      // Prevent multiple instantiations on re-renders
      if (addressInputRef.current.dataset.hasAutocomplete) return;
      addressInputRef.current.dataset.hasAutocomplete = true;

      const autocomplete = new window.google.maps.places.Autocomplete(
        addressInputRef.current,
        { types: ['address'], componentRestrictions: { country: 'ca' } }
      );

      autocomplete.addListener('place_changed', () => {
        // Temporarily disable the standard onChange to prevent double-firing
        const originalOnChange = onChangeRef.current;
        onChangeRef.current = () => {};

        const place = autocomplete.getPlace();

        if (!place.address_components) return;

        let streetNumber = '';
        let route = '';
        let city = '';
        let province = '';
        let postalCode = '';

        place.address_components.forEach(component => {
          const types = component.types;
          if (types.includes('street_number')) streetNumber = component.long_name;
          if (types.includes('route')) route = component.long_name;
          if (types.includes('locality')) city = component.long_name;
          if (types.includes('administrative_area_level_1')) province = component.short_name;
          if (types.includes('postal_code')) postalCode = component.long_name;
        });

        if (onPlaceSelectedRef.current) {
          onPlaceSelectedRef.current({
            address: `${streetNumber} ${route}`.trim() || place.name || '',
            city: city,
            province: province,
            postal_code: postalCode
          });
        }

        // Restore the original onChange handler after a short delay
        setTimeout(() => { onChangeRef.current = originalOnChange; }, 100);
      });
    };

    if (!window.google) {
      let script = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
      if (!script) {
        script = document.createElement('script');
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      script.addEventListener('load', initAutocomplete);
      return () => script.removeEventListener('load', initAutocomplete);
    } else {
      initAutocomplete();
    }
  }, []);

  return (
    <>
      <style>{`
        .pac-container {
          z-index: 9999 !important;
        }
      `}</style>
      <input
        type="text"
        name="address"
        className={className}
        value={value}
        onChange={(e) => onChangeRef.current && onChangeRef.current(e)}
        ref={addressInputRef}
        placeholder={placeholder || "Start typing an address..."}
        required={required}
      />
    </>
  );
}

export default AddressAutocomplete;